<script setup lang="ts">
import {
  useSkyBreakpoint,
  SkyPage,
  SkyFullSpan,
  SkyIcon,
} from '@groveco/skylight'
import { useDebounceFn } from '@vueuse/core'

import type { ContentfulEntry } from '~/types/contentful'
import { ROUTES } from '~/constants/routes'
import isMobile from '~/services/mobile'

/*
 * The Visitor Home Page
 * This page is responsible for the Grove homepage for site visitors who have
 * not checked out yet.
 */

// Set the page meta data
definePageMeta({
  showNotificationBar: true,
  // Setting this to true sets the main content pane to a blank slate without
  // container styles. This allows the page to accommodate content that is flush
  // to header, footer, or edges of the main pane.
  containsFlushContent: true,
  middleware: () => {
    if (process.server) {
      return
    }
    const { hasPlacedOrder, id } = useCustomer()
    const maybeRedirect = async () => {
      try {
        await resolveRef({
          ref: id,
          timeout: 5000,
        })
        if (hasPlacedOrder.value) {
          navigateTo(
            { path: ROUTES.memberHome.to, query: useRoute().query },
            { external: true }
          )
        }
      } catch (error) {
        const { $sentry } = useNuxtApp()
        $sentry.captureMessage('Could not fetch customer.', 'warning')
      }
    }
    maybeRedirect()
  },
})

useHead({
  title:
    'Cleaning & Household Products for a Sustainable Home | Grove Collaborative',
  meta: [
    {
      name: 'description',
      content:
        'Grove Collaborative delivers home essentials, including household cleaning, personal care, beauty, and pet products, for a more eco-friendly and sustainable lifestyle.',
    },
  ],
})

const hasSeenIncentiveModal = ref(false)
const inactivityTimer = ref()
const showPulse = ref(true)
const showRoundModal = ref(false)
const newPosition = ref(0)

const showFloatingActionButton = ref(true)

const { isMediumUp } = useSkyBreakpoint()
const HAS_SEEN_INCENTIVE_MODAL = 'HAS_SEEN_INCENTIVE_MODAL'
const INACTIVITY_TIME = ref(5 * 1000)
const AUTO_OPEN_SCROLL_DISTANCE = 200
const SCROLL_DEBOUNCE_WAIT = ref(50)
const FAST_SCROLLUP_TIMEOUT = ref(100)

const {
  title: offerTitle,
  description: offerDescription,
  image: offerImage,
  isOfferApplied,
} = useOffer()

const { email } = useCustomer()

const clearInactivityTimer = () => {
  clearTimeout(inactivityTimer.value)
}

const checkInactivity = () => {
  inactivityTimer.value = setTimeout(() => {
    openModal()
  }, INACTIVITY_TIME.value)

  window.onmousemove = clearInactivityTimer
  window.onmousedown = clearInactivityTimer
  window.ontouchstart = clearInactivityTimer
  window.onclick = clearInactivityTimer
  window.onkeydown = clearInactivityTimer
}

const handleShowOnFastScrollUp = useDebounceFn(() => {
  if (!hasSeenIncentiveModal.value && isFastScrollUp()) {
    openModal()
  }
}, SCROLL_DEBOUNCE_WAIT.value)

const isFastScrollUp = () => {
  const lastPosition = window.scrollY

  setTimeout(() => {
    newPosition.value = window.scrollY
  }, FAST_SCROLLUP_TIMEOUT.value)

  const currentSpeed = newPosition.value - lastPosition

  return currentSpeed > AUTO_OPEN_SCROLL_DISTANCE
}

const handleMouseLeave = () => {
  if (isOfferApplied.value && !email.value && !hasSeenIncentiveModal.value) {
    openModal()
  }
}

onMounted(() => {
  try {
    const modalAlreadyShown = window.localStorage.getItem(
      HAS_SEEN_INCENTIVE_MODAL
    )
    if (modalAlreadyShown) {
      showPulse.value = false
      hasSeenIncentiveModal.value = true
    }
  } catch (error) {
    // local storage errors such as quota exceeded or localStorage API is not supported.
  }

  if (!hasSeenIncentiveModal.value) {
    // check for auto open of incentive modal
    checkInactivity()
    if (isMobile()) {
      window.addEventListener('scroll', handleShowOnFastScrollUp)
    } else {
      document.documentElement.addEventListener('mouseleave', handleMouseLeave)
    }
  }
})

const removeListeners = () => {
  if (isMobile()) {
    window.removeEventListener('scroll', handleShowOnFastScrollUp)
  } else {
    document.documentElement.removeEventListener('mouseleave', handleMouseLeave)
  }
  clearInactivityTimer()
}

onUnmounted(() => {
  removeListeners()
})

const FULL_WIDTH_COMPONENTS = ['imageCtaHero', 'basicValuePropsWithHeaderImage']
const renderFullWidthAlways = (entry: ContentfulEntry) => {
  return FULL_WIDTH_COMPONENTS.includes(entry.contentTypeId)
}

const openModal = () => {
  hasSeenIncentiveModal.value = true
  showPulse.value = false
  showRoundModal.value = true
  removeListeners()
  try {
    window.localStorage.setItem(HAS_SEEN_INCENTIVE_MODAL, '1')
  } catch (error) {
    // local storage errors such as quota exceeded or localStorage API is not supported.
  }
}

const route = useRoute()
watchEffect(() => {
  if (process.client && route.query.fab === 'open' && isOfferApplied.value) {
    openModal()
  }
})

const handleClose = (onSuccess = false) => {
  showRoundModal.value = false
  if (onSuccess) {
    showFloatingActionButton.value = false
  }
}
</script>

<template>
  <NuxtLayout>
    <ContentDoc>
      <template #default="{ doc }">
        <div class="HomePage">
          <SkyPage class="HomePage_CTAHeroBlock">
            <SkyFullSpan
              :key="doc.content[0].id"
              :small="renderFullWidthAlways(doc.content[0])"
              :medium="renderFullWidthAlways(doc.content[0])"
              :large="renderFullWidthAlways(doc.content[0])"
            >
              <ContentfulContent :entries="[doc.content[0]]" />
            </SkyFullSpan>
            <SkyFullSpan small medium large>
              <WidgetCategoryCarousel data-test-id="category-carousel" />
            </SkyFullSpan>
          </SkyPage>
          <SkyPage>
            <SkyFullSpan
              v-for="entry in doc.content.slice(1)"
              :key="entry.id"
              :small="
                renderFullWidthAlways(entry) ||
                entry.contentTypeId === 'basicProductCarousel'
              "
              :medium="renderFullWidthAlways(entry)"
              :large="renderFullWidthAlways(entry)"
            >
              <ContentfulContent :entries="[entry]" />
            </SkyFullSpan>
          </SkyPage>
        </div>
      </template>
      <template #not-found>
        {{ showErrorPage('No content found') }}
      </template>
    </ContentDoc>
    <button
      v-if="isOfferApplied && !email && showFloatingActionButton"
      class="HomePage_FloatingActionButton"
      :class="{ HomePage_FloatingActionButton_Pulse: showPulse }"
      data-test-id="floating-action-button"
      tabindex="1"
      aria-label="Open gift offer"
      @click="openModal"
      @keyup.enter="openModal"
    >
      <SkyIcon
        class="HomePage_FloatingActionButton_Icon"
        name="gift"
        title="gift icon"
        :size="isMediumUp ? '32px' : '24px'"
        role="presentation"
      />
      <div class="HomePage_FloatingActionButton_Text">FREE</div>
    </button>
    <RoundModal
      v-if="showRoundModal"
      :heading="offerTitle"
      :subhead="offerDescription"
      :image-src="offerImage"
      data-test-id="round-modal"
      @close="handleClose"
    />
  </NuxtLayout>
</template>

<style lang="scss">
.HomePage {
  // For the image cta hero and category carousel, take out the bottom row gap applied on each SkyFullSpan element by SkyPage.
  // Some temporary hacks that can be refined later.

  margin-bottom: var(--spacing-10x);
  min-height: 95vh;

  &_CTAHeroBlock {
    row-gap: 0;
    margin-bottom: var(--spacing-6x);
  }
}

.HomePage_FloatingActionButton {
  position: fixed;
  border: none;
  bottom: var(--spacing-2x);
  right: var(--spacing-2x);
  height: var(--spacing-16x);
  width: var(--spacing-16x);
  border-radius: var(--border-radius-pill);
  background-color: var(--surface-color-leaf);
  z-index: var(--z-index-sticky);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @include for-medium-up {
    height: var(--spacing-24x);
    width: var(--spacing-24x);
    bottom: var(--spacing-12x);
    right: var(--spacing-12x);
  }

  &_Icon {
    margin-bottom: var(--spacing-1x);
    fill: var(--text-color-savings);
  }

  &_Text {
    @include type-section('s');

    @include for-medium-up {
      font-size: var(--font-size-75);
      letter-spacing: var(--letter-spacing-loose);
    }
  }

  &_Pulse {
    animation: pulse-animation 2s infinite;
    animation-iteration-count: 5;
  }

  @keyframes pulse-animation {
    0% {
      box-shadow: 0 0 0 0 var(--surface-color-leaf);
    }
    100% {
      box-shadow: 0 0 0 var(--spacing-6x) rgba(0, 0, 0, 0);
    }
  }
}
</style>
